import * as React from 'react';
import './index.css';
import KNB from '@wmfe/mach-pro-knb';
import { i18nClient } from '@sailor/i18n-mach-pro';
import { reportRaptorMetrics } from '@/src/common/SLRaptor';
import { ImageGridMode } from './types';

const DEFAULT_MAX_COUNT = 9;
const DEFAULT_ROW_COUNT = 3;

interface IState {
  maxCount: number;
  empowerVisible: boolean;
}

interface IProps {
  initialImages?: string[];
  mode: string;
  maxCount?: number;
  onUpload?: (images: string[]) => void; //每次上传完触发，参数为当前所有图片 url
  onRemove?: (index: number) => void;
  onUploadFailed?: () => void; // 上传失败时触发
}

export default class ImageGridView extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);
    this.state = {
      empowerVisible: false,
      maxCount: this.props.maxCount ?? DEFAULT_MAX_COUNT,
    };
  }

  isUploadMode = () => {
    const { mode } = this.props;
    return mode === ImageGridMode.UPLOAD;
  };

  // 删除上传的图片
  delUploadImgClick = (index: number) => {
    this.props.onRemove && this.props.onRemove(index);
  };

  // 上传图片 最多上传 maxCount 张图片
  uploadImg = () => {
    const { initialImages, onUpload, onUploadFailed } = this.props;
    const images = initialImages || [];

    if (images && images.length >= this.state.maxCount) {
      return;
    }

    // @ts-ignore
    const indicatorModule = Mach.requireModule('ActivityIndicator');
    indicatorModule.showLoading();
    KNB.chooseImage({
      source: 'gallery', //图片类型：'gallery'相册, 'camera'相机，''相机相册
      count: this.state.maxCount - images?.length,
      success: (result) => {
        const localIds = result.photoInfos.map((photo) => photo.localId);
        if (localIds?.length === 0) {
          indicatorModule.hideLoading();
          return;
        }
        KNB.uploadImage({
          // sceneToken: '*', // 受隐私合规中长期方案影响 新增sceneToken字段，具体权限参考下表
          localIds,
          bucket: 'sailorc', // 如未指定，默认会用'knb'的bucket.
          clientId: 'vxcwmfx2tcwrbdnc000000000072651e', // 如指定了 bucket ，则需要配套指定 clientID.
          success: (r) => {
            let resPhotoCount = 0;
            r.photoInfos.map((it) => {
              // @ts-ignore 如有返回正常的url,保存在img数组中
              if (it?.picUrl) {
                resPhotoCount++;
                // @ts-ignore
                images.push(it?.picUrl);
              }
            });
            // 如果存在null的下发的时候,images 数量少,提示图片上传,其余正常下发的图片正常保存
            if (resPhotoCount !== r.photoInfos.length) {
              onUploadFailed?.();
            }
            onUpload?.(images);
            indicatorModule.hideLoading();
            this.reportUploadImageMonitor(true);
          },
          fail: (reason: any) => {
            indicatorModule.hideLoading();
            this.reportUploadImageMonitor(false, reason?.errorCode ?? -1);
            onUploadFailed?.();
          },
        });
      },
      fail: (rlt: any) => {
        this.setState({ empowerVisible: true });
        indicatorModule.hideLoading();
        if (rlt.errorCode === 890) {
          this.setState({ empowerVisible: true });
        }
        //console.log(` fail to choose image. ${JSON.stringify(rlt)}`)
        // WMToast?.show(picupLoadErrorTitle, 2);
      },
    });
  };

  // 申请相册权限
  reqeustPhotoPermission = () => {
    KNB.use('requestPermission', {
      sceneToken: 'sailor_c_privacy_token',
      type: Mach.env.isIOS ? 'photos' : 'storage', // 权限类型，支持选项参考下表
      readonly: false, // 如果为 true，将只读权限，不做申请权限的尝试
      forceJump: false, // 默认值为false，为false时会询问用户，向用户申请权限。如果为 true，将直接唤起系统设置面板。已废弃
      success: (res) => {
        // 拥有该权限，或者唤起请求面板后用户允许支持该权限，安卓端需要再次请求相机的权限
        if (Mach.env.isAndroid) {
          this.requestCameraPermission();
        } else {
          this.setState({ empowerVisible: false });
        }
      },
      fail: (res) => {
        this.setState({ empowerVisible: false });
        KNB.use('openAppSetting', {
          fail(msg) {
            //console.log(msg)
          },
        });
        // 没有该权限，或者唤起请求面板后用户拒绝该权限
      },
    });
  };

  // 申请相机权限
  requestCameraPermission = () => {
    KNB.use('requestPermission', {
      sceneToken: 'sailor_c_privacy_token',
      type: 'camera', // 权限类型，支持选项参考下表
      readonly: false, // 如果为 true，将只读权限，不做申请权限的尝试
      forceJump: false, // 默认值为false，为false时会询问用户，向用户申请权限。如果为 true，将直接唤起系统设置面板。已废弃
      success: (res) => {
        // 拥有该权限，或者唤起请求面板后用户允许支持该权限
        this.setState({ empowerVisible: false });
      },
      fail: (res) => {
        this.setState({ empowerVisible: false });
        KNB.use('openAppSetting', {
          fail(msg) {
            //console.log(msg)
          },
        });
      },
    });
  };

  /**
   * 上传图片监控指标
   * @param success
   */
  reportUploadImageMonitor = (success: boolean, code = 0) => {
    const params = {
      SLUploadImageRate: success ? 1 : 0
    };
    const tags = {
      code: `${code}`
    };
    reportRaptorMetrics(params, tags, 1.0);
  }

  render() {
    const { initialImages } = this.props;
    const images = initialImages || [];
    const showUploadBtn = images?.length < this.state.maxCount && this.isUploadMode();

    const screenWidth = Mach.env?.screenWidth; // 屏幕宽度
    const pagePadding = (16 + 12) * 2; // 页面左右边距
    const space = 8 * (DEFAULT_ROW_COUNT - 1); // 图片间距

    let width: number;

    if (this.isUploadMode()) {
      width = (screenWidth - 48 * 2) / DEFAULT_ROW_COUNT;
    } else { // 非上传模式 适配宽度
      width = (screenWidth - pagePadding - space) / DEFAULT_ROW_COUNT;
    }

    const occupiedSpaceCount = images.length + (showUploadBtn ? 1 : 0);
    const remainSpaceCount = (DEFAULT_ROW_COUNT - (occupiedSpaceCount % DEFAULT_ROW_COUNT)) % DEFAULT_ROW_COUNT;
    const empyItmes = new Array(remainSpaceCount).fill(1).map((v, i) => i);

    return (
      <view className="upImageArea">
        {images?.length > 0
          && images.map((it: any, index: number) => (
            <view key={it} className="upImageItem" style={{ width, height: width }}>
              <image className="upImage" src={it} />
              {this.isUploadMode() && (
                <view
                  className="del-view"
                  onClick={() => {
                    this.delUploadImgClick(index);
                  }}
                >
                  <image className="del-image-btn" src="assets://review/icon_delete.png" />
                </view>
              )}
            </view>
          ))}
        {showUploadBtn && (
          <view
            className="uploadImg"
            style={{ width, height: width }}
            onClick={() => {
              this.uploadImg();
            }}
          >
            <image src="assets://imagegroup/icon_upload.png" className="cameraImg" />
            <text className="cameraTitle">{i18nClient.getText('review_photo_Upload')}</text>
          </view>
        )}
        {empyItmes?.map((it) => <view key={it} className="emptyView" style={{ width, height: width }} />)}
      </view>
    );
  }
}
